import _get from 'lodash/get';
import {
  AppUserAvailableInterventionsQueryVariables,
  Asset,
  FilterGroupingEnum,
  Intervention,
  InterventionFilterInputObject,
  InterventionScopeNameEnum,
  Site,
  SiteArea,
} from 'graphql-common';
import { APP_URLS } from 'constants/urls';
import { ErrorPageStatusEnum } from '@lib/components/ErrorPage/enums';
import InterventionsFilterFieldNames from '@lib/enums/fieldNames/interventionsFilterFieldNames';
import { ScannerActionType } from 'routes/Scanner/enums';
import QrCodePrefix from '@lib/enums/qrCodePrefix';
import isErrorTimeoutError from '@lib/utils/isErrorTimeoutError';
import { logger } from '@lib/services/logger';
import { CodeType } from './getCodeType';
import { ParsedCodes } from './getParsedCodes';
import { HandleQrCodeSuccessProps } from './types';

const defaultVariables: AppUserAvailableInterventionsQueryVariables = {
  page: 1,
  limit: 2,
  interventionScope: InterventionScopeNameEnum.All,
  interventionFilters: undefined,
  siteFindBy: { code: undefined },
  assetFindBy: { code: undefined },
  siteAreaFindBy: { code: undefined },
};

export default async function handleValidCodeForInterventionsAction(
  data: string,
  {
    navigate,
    fetchAppInterventions,
    setLoading,
    setErrorStatus,
    onSuccessScanAction,
  }: HandleQrCodeSuccessProps,
  parsedCodes: ParsedCodes,
  codeType: CodeType,
) {
  try {
    const filters: InterventionFilterInputObject[] = [];
    const { customPrefix, entityCode, sideCode } = parsedCodes;

    const variables = {
      ...defaultVariables,
    };

    if (codeType !== CodeType.Invalid) {
      if (sideCode) {
        variables.siteFindBy.code = sideCode;
        filters.push({
          siteCode: {
            grouping: FilterGroupingEnum.And,
            predicate: { in: [sideCode] },
            filterGroup: 'siteAreaAssetFilerGroup',
          },
        });
      }
      if (entityCode) {
        variables.siteAreaFindBy.code = entityCode;
        variables.assetFindBy.code = entityCode;
        if (codeType === CodeType.SiteArea) {
          filters.push({
            siteAreaCode: {
              grouping: FilterGroupingEnum.And,
              predicate: { in: [entityCode] },
              filterGroup: 'siteAreaAssetFilerGroup',
            },
          });
        }
        if (codeType === CodeType.Asset) {
          filters.push({
            assetCode: {
              grouping: FilterGroupingEnum.And,
              predicate: { in: [entityCode] },
              filterGroup: 'siteAreaAssetFilerGroup',
            },
          });
        }
        if (
          codeType === CodeType.Asset &&
          customPrefix &&
          customPrefix !== QrCodePrefix.SiteArea &&
          customPrefix !== QrCodePrefix.Asset
        ) {
          filters.push({
            assetQrPrefix: {
              grouping: FilterGroupingEnum.And,
              predicate: { in: [customPrefix] },
              filterGroup: 'siteAreaAssetFilerGroup',
            },
          });
        }
      }
    }
    if (filters.length) {
      variables.interventionFilters = filters;
    }
    logger.debug('Scanner: find interventions - querying API', {
      filters,
      codeType,
    });

    setLoading(true);
    const res = await fetchAppInterventions({
      variables,
      errorPolicy: 'ignore',
    });
    const { data: userAvailableData, error } = res;
    const isTimeoutError = isErrorTimeoutError(error);
    if (isTimeoutError) {
      logger.warn('Scanner: find interventions - API timeout');
      setErrorStatus(ErrorPageStatusEnum.QrCodeScanFailedOffline);
    } else if (error) {
      logger.error('Scanner: find interventions - API error', {
        error: error.message,
      });
      setErrorStatus(ErrorPageStatusEnum.QrCodeScanFailed);
    } else {
      const collection = _get(
        userAvailableData,
        'interventions.collection',
        [],
      ) as Intervention[];
      const site = _get(userAvailableData, 'site') as Site;
      const siteArea = _get(userAvailableData, 'siteArea') as SiteArea;
      const asset = _get(userAvailableData, 'asset') as Asset;

      logger.debug('Scanner: find interventions - API response', {
        interventionsCount: collection.length,
        hasSite: !!site,
        hasSiteArea: !!siteArea,
        hasAsset: !!asset,
        codeType,
      });

      setLoading(false);
      if (codeType === CodeType.Asset && asset === null) {
        logger.warn(
          'Scanner: find interventions - asset not found for Asset code type',
        );
        setErrorStatus(ErrorPageStatusEnum.QrCodeScanFailed);
      } else if (collection.length === 1) {
        logger.info(
          'Scanner: find interventions - single intervention found, navigating',
          { interventionId: collection[0].id },
        );
        navigate(
          APP_URLS.app.interventions.view.getDynamicPath({
            pathParts: { id: collection[0].id },
          }),
        );
      } else if ((sideCode || entityCode) && (site || siteArea || asset)) {
        const values = {
          [InterventionsFilterFieldNames.Site]:
            sideCode && site
              ? [
                  {
                    value: site.id,
                    label: site.name,
                  },
                ]
              : undefined,
          [InterventionsFilterFieldNames.SiteArea]:
            entityCode && siteArea && codeType === CodeType.SiteArea
              ? [
                  {
                    value: siteArea.id,
                    label: siteArea.name,
                  },
                ]
              : undefined,
          [InterventionsFilterFieldNames.Asset]:
            entityCode && asset && codeType === CodeType.Asset
              ? [
                  {
                    value: asset.id,
                    label: asset.name,
                  },
                ]
              : undefined,
        };
        logger.info(
          'Scanner: find interventions - navigating to list with filters',
          {
            siteId: site?.id,
            siteName: site?.name,
            siteAreaId: siteArea?.id,
            siteAreaName: siteArea?.name,
            assetId: asset?.id,
            assetName: asset?.name,
          },
        );
        onSuccessScanAction(values, ScannerActionType.FindInterventions);
      } else {
        logger.warn(
          'Scanner: find interventions - no matching interventions or entities found',
        );
        setErrorStatus(ErrorPageStatusEnum.QrCodeScanFailed);
      }
    }
  } catch (error) {
    logger.error('Scanner: find interventions - unexpected error', {
      error: error instanceof Error ? error.message : String(error),
    });
    setErrorStatus(ErrorPageStatusEnum.QrCodeScanFailed);
  }
}
